import throttle from 'lodash/throttle';
import { useEffect } from 'react';

import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import { sendableSteps } from '@atlaskit/prosemirror-collab';
import type { Step } from '@atlaskit/editor-prosemirror/transform';

import { usePageState } from '@confluence/page-context';

import type { EditorOfflineStoragePlugin } from './editorOfflineStoragePluginType';
import { StepsDatabase } from './stepsDatabase';
import { createPlugin } from './pm-plugins/main';

export const editorOfflineStoragePlugin: EditorOfflineStoragePlugin = ({ api }) => {
	const stepsDatabase = new StepsDatabase();
	return {
		name: 'editorOfflineStorage',
		pmPlugins() {
			return [
				{
					name: 'editorOfflineStoragePlugin',
					plugin: createPlugin(),
				},
			];
		},
		usePluginHook: () => {
			const [{ contentId, contentIdLoading }] = usePageState();
			useEffect(() => {
				if (contentId && !contentIdLoading) {
					void stepsDatabase.initDB(contentId);
				}
			}, [contentId, contentIdLoading]);

			usePluginStateEffect(
				api,
				['editorOfflineStorage', 'connectivity', 'collabEdit'],
				({ editorOfflineStorageState, connectivityState, collabEditState }) => {
					if (
						window?.indexedDB &&
						editorOfflineStorageState?.steps &&
						connectivityState?.mode === 'offline' &&
						collabEditState?.initialised.collabInitialisedAt
					) {
						throttledUpdate(editorOfflineStorageState.steps, stepsDatabase);
					}
				},
			);
		},
		getSharedState(state) {
			if (!state) {
				return undefined;
			}
			return {
				steps: sendableSteps(state)?.steps ?? [],
			};
		},
	};
};

const throttledUpdate = throttle((steps: readonly Step[], stepsDatabase: StepsDatabase) => {
	void stepsDatabase.updateSteps(steps);
}, 200);
